/**
 * Vanturis Analytics — centralized GA4 wrapper.
 *
 * Design notes
 * ------------
 * * Loads gtag.js exactly once, ONLY when
 *   `process.env.REACT_APP_GA_MEASUREMENT_ID` is populated.
 * * `send_page_view: false` is passed to `gtag('config', ...)` — page
 *   views are sent manually per SPA route change so we never emit two
 *   events for the same route.
 * * Every path passes through `sanitizePath()` which:
 *     - strips query strings + fragments
 *     - replaces the token segment on `/account/activate/:token` with
 *       `/account/activate/[redacted]`
 *     - blocks tracking entirely on `/admin`, `/internal-access`, and
 *       any path with a suspiciously token-shaped segment.
 * * Every call is wrapped in try/catch and swallows errors — analytics
 *   MUST NOT break rendering, navigation, or auth.
 * * PII is never sent: no email, name, phone, user id, token, or
 *   form-field value.  Custom events accept only whitelisted category
 *   strings.
 * * Privacy config: no ad-personalization, no Google Signals, no
 *   remarketing, no user-id tracking, no cross-domain, no form-value
 *   auto-capture.
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

// P1D (final): deterministic post-critical load of the heavy gtag.js (~190 KB).
// The dataLayer QUEUE is created immediately (cheap array pushes, no network);
// the actual Google library is injected exactly ONCE at `window.load` +
// GA_LOAD_DELAY_MS. Generic browsing (scroll/touch/pointer/key/mousemove) may
// queue analytics but MUST NOT accelerate the heavy library — that would push
// Google's ~150-220ms evaluation right after a user's first interaction and
// hurt responsiveness / INP. gtag.js replays everything already queued in
// window.dataLayer on load, so queued page_views + config fire in order with
// no duplication.
//
// Known limitation (documented, accepted): a session that ends BEFORE the
// scheduled load (window.load + 3500ms) with no conversion cannot deliver its
// page_view — there is no lightweight, standards-safe, already-supported way
// to transmit it without loading the heavy library during startup, so we
// prefer site performance. (A pagehide inject cannot be proven to transmit
// before teardown, so it is intentionally NOT used as a lifecycle guarantee.)
const GA_LOAD_DELAY_MS = 3500;

let _queueReady = false;     // dataLayer + gtag shim + js/config pushed
let _scriptRequested = false; // real gtag.js <script> injected once
let _loadScheduled = false;  // deferred-load triggers attached once
let _lastPath = null;

const BLOCKED_PATH_PREFIXES = [
  "/admin",
  "/internal-access",
  "/api",
  "/preview-debug",
];

// Route patterns whose ID/token segment must be scrubbed before send.
// Extend when new sensitive routes appear.
const REDACT_TOKEN_ROUTES = [
  { prefix: "/account/activate/", replaceWith: "/account/activate/[redacted]" },
  { prefix: "/account/reset/", replaceWith: "/account/reset/[redacted]" },
  { prefix: "/account/invite/", replaceWith: "/account/invite/[redacted]" },
];

function isEnabled() {
  return Boolean(MEASUREMENT_ID);
}

/**
 * Return true when the current pathname is a sensitive token-bearing
 * route where gtag.js MUST NOT load.  Enhanced Measurement in GA4
 * captures `page_location` on script-load; scrubbing our own
 * page_view is not enough — we must never load gtag on these routes.
 */
function isSensitiveActivationPath(pathname) {
  if (!pathname) return false;
  for (const rule of REDACT_TOKEN_ROUTES) {
    if (pathname.startsWith(rule.prefix)) return true;
  }
  return false;
}

function currentPathname() {
  try {
    return (window && window.location && window.location.pathname) || "/";
  } catch {
    return "/";
  }
}

function isBlockedPath(pathname) {
  if (!pathname) return true;
  return BLOCKED_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname === p);
}

/**
 * Strip query + fragment, redact known-sensitive segments.
 * Never throws.  Returns a safe path string that is OK to transmit.
 */
export function sanitizePath(rawPath) {
  try {
    if (!rawPath) return "/";
    // If a full URL slipped in, keep only the pathname.
    let path = rawPath;
    if (/^https?:\/\//i.test(rawPath)) {
      try {
        path = new URL(rawPath).pathname;
      } catch {
        path = rawPath;
      }
    }
    // Kill query + fragment.
    path = path.split("?", 1)[0].split("#", 1)[0];
    // Redact known token routes.
    for (const rule of REDACT_TOKEN_ROUTES) {
      if (path.startsWith(rule.prefix) && path.length > rule.prefix.length) {
        path = rule.replaceWith;
        break;
      }
    }
    return path || "/";
  } catch {
    return "/";
  }
}

/**
 * Create the dataLayer queue + gtag shim and push js/config ONCE.
 * Cheap: only array pushes, no network, no library. Safe to call repeatedly.
 */
function _setupQueue() {
  if (_queueReady || !isEnabled()) return;
  try {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== "function") {
      window.gtag = function () { window.dataLayer.push(arguments); };
    }
    window.gtag("js", new Date());
    // Manual page-view control + privacy-conservative defaults.
    window.gtag("config", MEASUREMENT_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      anonymize_ip: true,
    });
    _queueReady = true;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("[analytics] queue setup failed", err);
  }
}

/**
 * Inject the real gtag.js library exactly once. gtag.js processes the
 * already-queued window.dataLayer on load, flushing queued page_views in
 * order. HARD BLOCK on token-bearing routes (load itself sends page_location).
 */
function _injectGtagScript() {
  if (_scriptRequested || !isEnabled()) return;
  if (isSensitiveActivationPath(currentPathname())) return;
  try {
    _setupQueue();
    if (!document.getElementById("ga4-gtag")) {
      const s = document.createElement("script");
      s.async = true;
      s.id = "ga4-gtag";
      s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
      document.head.appendChild(s);
    }
    _scriptRequested = true;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("[analytics] gtag inject failed", err);
  }
}

/**
 * Attach the single deterministic load trigger: window `load` + delay.
 * Generic interaction and pagehide are intentionally NOT triggers — browsing
 * must never accelerate the heavy Google library.
 */
function _scheduleGtagLoad() {
  if (_loadScheduled || !isEnabled()) return;
  if (typeof window === "undefined") return;
  _loadScheduled = true;
  const startTimer = () => { setTimeout(_injectGtagScript, GA_LOAD_DELAY_MS); };
  if (typeof document !== "undefined" && document.readyState === "complete") startTimer();
  else window.addEventListener("load", startTimer, { once: true });
}

/**
 * Conversion events may load GA sooner than the normal grace period — but
 * NEVER synchronously inside the user's handler. The event is queued first
 * (by the caller), then the library load is scheduled on a fresh task so the
 * interaction + paint complete before any Google download/evaluation.
 */
function _scheduleConversionLoad() {
  if (typeof window === "undefined") { _injectGtagScript(); return; }
  setTimeout(_injectGtagScript, 0);
}

/**
 * Initialize analytics on app boot. Idempotent + cheap: creates the
 * dataLayer queue and schedules the deferred gtag.js load. The heavy Google
 * library is NEVER downloaded/executed during app startup.
 */
export function initAnalytics() {
  if (!isEnabled()) return;
  if (isSensitiveActivationPath(currentPathname())) return;
  _setupQueue();
  _scheduleGtagLoad();
}

/**
 * Record a page view for the given path.  De-duplicates repeat calls
 * for the same path.  Returns true when an event was sent.
 */
export function trackPageView(rawPath) {
  if (!isEnabled()) return false;
  if (isBlockedPath(rawPath)) return false;
  if (isSensitiveActivationPath(rawPath)) return false;
  const path = sanitizePath(rawPath);
  if (path === _lastPath) return false;
  _lastPath = path;
  try {
    // Queue immediately (cheap); the heavy gtag.js library is loaded later
    // by _scheduleGtagLoad() and replays this event in order.
    _setupQueue();
    _scheduleGtagLoad();
    window.gtag && window.gtag("event", "page_view", {
      page_path: path,
      page_location: `${window.location.origin}${path}`,
      page_title: (document && document.title) || "Vanturis",
    });
    return true;
  } catch {
    return false;
  }
}

const ALLOWED_EARLY_ACCESS_RESULTS = new Set(["success", "validation_error", "server_error", "network_error"]);

/**
 * Track a coming-soon / early-access form event.  Accepts only
 * whitelisted category strings — never form-field values, emails,
 * names, phones, notes, etc.
 */
export function trackEarlyAccess(eventName, formLocation = "coming_soon", resultCategory = null) {
  if (!isEnabled()) return false;
  const allowedNames = ["early_access_submit", "early_access_success", "early_access_error"];
  if (!allowedNames.includes(eventName)) return false;
  const payload = {
    page_path: sanitizePath((window && window.location && window.location.pathname) || "/"),
    form_location: String(formLocation || "").slice(0, 40),
  };
  if (resultCategory && ALLOWED_EARLY_ACCESS_RESULTS.has(resultCategory)) {
    payload.result_category = resultCategory;
  }
  try {
    // Queue the conversion first (cheap), then load GA on a fresh task so the
    // user's interaction + paint finish before any Google download/evaluation.
    _setupQueue();
    window.gtag && window.gtag("event", eventName, payload);
    _scheduleConversionLoad();
    return true;
  } catch {
    return false;
  }
}

// ── Public marketplace inquiry analytics ─────────────────────────────
// STRICT allow-list — no PII, no listing IDs, no inquiry references, no
// raw errors.  Only sanitized page path, form location, safe result
// category, and (optionally) an asset category string that is already
// displayed publicly on the page anyway.
const ALLOWED_PUBLIC_INQUIRY_RESULTS = new Set([
  "success", "validation_error", "rate_limited",
  "duplicate", "server_error", "network_error", "listing_unavailable",
]);
const ALLOWED_PUBLIC_INQUIRY_EVENTS = new Set([
  "public_inquiry_submit", "public_inquiry_success", "public_inquiry_error",
]);

/**
 * Track a public-marketplace inquiry event.  Values are strictly
 * whitelisted; anything not on the list is silently dropped.  This
 * function NEVER accepts email/name/phone/message/listing-party
 * information or any raw error string.
 */
export function trackPublicInquiry(eventName, formLocation = "listing_detail",
                                     resultCategory = null,
                                     assetCategory = null) {
  if (!isEnabled()) return false;
  if (!ALLOWED_PUBLIC_INQUIRY_EVENTS.has(eventName)) return false;
  const payload = {
    page_path: sanitizePath((window && window.location && window.location.pathname) || "/"),
    form_location: String(formLocation || "").slice(0, 40),
  };
  if (resultCategory && ALLOWED_PUBLIC_INQUIRY_RESULTS.has(resultCategory)) {
    payload.result_category = resultCategory;
  }
  if (assetCategory) {
    // Only accept a short, alphanumeric category string.
    const cat = String(assetCategory).slice(0, 40).replace(/[^A-Za-z0-9_-]/g, "");
    if (cat) payload.asset_category = cat;
  }
  try {
    // Queue the conversion first (cheap), then load GA on a fresh task so the
    // user's interaction + paint finish before any Google download/evaluation.
    _setupQueue();
    window.gtag && window.gtag("event", eventName, payload);
    _scheduleConversionLoad();
    return true;
  } catch {
    return false;
  }
}

// Exposed for tests only.
export const __private = { isBlockedPath, isEnabled, MEASUREMENT_ID };
